<template>
  <div class="newsflash-admin-page">
    <div class="newsflash-admin-container">
      <header class="admin-header">
        <div>
          <h1>Gestion noticias flash</h1>
          <p>{{ headerDescription }}</p>
        </div>

        <div class="header-actions" v-if="!isJaReview">
          <button type="button" class="secondary-button" @click="loadCurrentView()">
            <span class="material-icons-outlined">refresh</span>
            Actualizar
          </button>
          <button type="button" class="primary-button" :disabled="actionLoading" @click="runCapture">
            <span class="material-icons-outlined">bolt</span>
            Buscar noticias ahora
          </button>
          <button type="button" class="danger-button" :disabled="actionLoading" @click="clearQueue">
            <span class="material-icons-outlined">delete_sweep</span>
            Vaciar no publicadas
          </button>
        </div>
      </header>

      <section v-if="loading" class="state-panel">
        <div class="loading-spinner"></div>
        <h2>Cargando gestion</h2>
        <p>Recuperando noticias pendientes.</p>
      </section>

      <section v-else-if="errorMessage" class="state-panel">
        <span class="material-icons-outlined state-icon">lock</span>
        <h2>No se puede acceder</h2>
        <p>{{ errorMessage }}</p>
        <button type="button" class="primary-button" @click="loadCurrentView()">Reintentar</button>
      </section>

      <section v-else-if="currentItem" class="detail-layout">
        <div class="detail-card">
          <div class="detail-topbar">
            <button v-if="!isJaReview" type="button" class="secondary-button" @click="goToQueue">
              <span class="material-icons-outlined">arrow_back</span>
              {{ detailBackLabel }}
            </button>
            <span class="status-chip">{{ currentItem.status_label }}</span>
          </div>

          <h2>{{ currentItem.title }}</h2>

          <div class="meta-grid">
            <div>
              <span>Medio</span>
              <strong>{{ currentItem.media }}</strong>
            </div>
            <div>
              <span>Volatilidad estimada</span>
              <strong>{{ formatVolatility(currentItem.volatility_percent) }}</strong>
            </div>
            <div>
              <span>Tipo de noticia</span>
              <strong>{{ currentItem.news_type_label }}</strong>
            </div>
            <div v-if="currentItem.filter_reason_label">
              <span>Motivo de descarte</span>
              <strong>{{ currentItem.filter_reason_label }}</strong>
            </div>
            <div>
              <span>Fecha de publicacion</span>
              <strong>{{ formatDate(currentItem.publication_date) }}</strong>
            </div>
            <div>
              <span>Fecha de caducidad</span>
              <strong>{{ formatTimestamp(currentItem.expires_at) }}</strong>
            </div>
            <div>
              <span>Fecha de generacion</span>
              <strong>{{ formatTimestamp(currentItem.created) }}</strong>
            </div>
          </div>

          <a class="source-link" :href="currentItem.source_url" target="_blank" rel="noopener">
            Abrir fuente
            <span class="material-icons-outlined">open_in_new</span>
          </a>

          <article class="content-block">
            <h3>Que ha pasado</h3>
            <p>{{ currentItem.what_happened }}</p>
          </article>

          <article class="content-block">
            <h3>Consecuencias economicas</h3>
            <p>{{ currentItem.economic_consequences }}</p>
          </article>

          <article class="content-block">
            <h3>Analisis interno JA</h3>
            <div class="internal-analysis-grid">
              <div>
                <span>Volatilidad</span>
                <strong>{{ formatVolatility(currentItem.volatility_percent) }}</strong>
              </div>
              <div>
                <span>Ambito</span>
                <strong>{{ currentItem.news_type_label }}</strong>
              </div>
              <div v-if="currentItem.sector">
                <span>Sector</span>
                <strong>{{ currentItem.sector }}</strong>
              </div>
              <div v-if="currentItem.historical_high_ticker">
                <span>Ticker cerca de extremo historico</span>
                <strong>{{ formatHistoricalHighTicker(currentItem.historical_high_ticker) }}</strong>
              </div>
            </div>
            <div v-if="currentItem.comparable_event" class="comparable-event">
              <span>Suceso comparable</span>
              <strong>{{ currentItem.comparable_event.fecha }} · {{ currentItem.comparable_event.activo_o_mercado_afectado }}</strong>
              <p>{{ currentItem.comparable_event.hecho }}</p>
              <p>{{ currentItem.comparable_event.movimiento_observado }}</p>
              <a v-if="currentItem.comparable_event.fuente_url" :href="currentItem.comparable_event.fuente_url" target="_blank" rel="noopener">
                Fuente del suceso
              </a>
            </div>
            <p v-if="currentItem.volatility_reason" class="analysis-note">{{ currentItem.volatility_reason }}</p>
            <p v-if="currentItem.filter_detail" class="analysis-note">{{ currentItem.filter_detail }}</p>
          </article>

          <article class="content-block">
            <h3>Valores afectados</h3>
            <div v-if="!currentItem.affected_stocks_count" class="empty-text">
              No hay valores relacionados.
            </div>
            <div v-else class="stocks-list">
              <div v-for="stock in currentItem.affected_stocks" :key="stock.ticker" class="stock-row">
                <div class="stock-head">
                  <strong>{{ stock.ticker }}</strong>
                  <span>{{ stock.nombre_empresa }}</span>
                </div>
                <p v-if="stock.a_que_se_dedica">{{ stock.a_que_se_dedica }}</p>
                <p>{{ stock.como_se_ve_afectado }}</p>
              </div>
            </div>
          </article>
        </div>

        <aside class="actions-panel">
          <h3>Acciones</h3>
          <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

          <template v-if="isJaReview">
            <button type="button" class="primary-button full-width" :disabled="actionLoading" @click="approveCurrent">
              <span class="material-icons-outlined">check_circle</span>
              Aprobar y publicar
            </button>
            <button type="button" class="danger-button full-width" :disabled="actionLoading" @click="rejectCurrent">
              <span class="material-icons-outlined">cancel</span>
              Denegar
            </button>
          </template>

          <template v-else>
            <p class="action-note">
              Esta vista es solo de seguimiento. Las noticias que superan el filtro se envian automaticamente a JA.
            </p>
          </template>
        </aside>
      </section>

      <section v-else class="queue-panel">
        <div class="admin-tabs">
          <button type="button" :class="{ active: activeSection === 'ja-pending' }" @click="setActiveSection('ja-pending')">
            <span class="material-icons-outlined">mark_email_unread</span>
            Pendientes JA
          </button>
          <button type="button" :class="{ active: activeSection === 'published' }" @click="setActiveSection('published')">
            <span class="material-icons-outlined">article</span>
            Publicadas
          </button>
          <button type="button" :class="{ active: activeSection === 'filtered' }" @click="setActiveSection('filtered')">
            <span class="material-icons-outlined">filter_alt_off</span>
            Descartadas
          </button>
          <button type="button" :class="{ active: activeSection === 'queue' }" @click="setActiveSection('queue')">
            <span class="material-icons-outlined">pending_actions</span>
            Capturadas sin enviar
          </button>
        </div>

        <template v-if="activeSection === 'queue'">
        <div class="queue-summary">
          <div class="queue-summary-main">
            <strong>{{ items.length }}</strong>
            <span>capturadas sin decision automatica</span>
          </div>
        </div>

        <p v-if="actionMessage" class="action-message queue-action-message">{{ actionMessage }}</p>

        <div v-if="!items.length" class="state-panel compact">
          <span class="material-icons-outlined state-icon">newspaper</span>
          <h2>No hay noticias capturadas sin enviar</h2>
          <p>Las noticias que superen el filtro pasaran directamente a pendientes de JA.</p>
        </div>

        <div v-else class="queue-list">
          <article
            v-for="item in items"
            :key="item.id"
            class="queue-item queue-item-no-select"
          >
            <router-link :to="{ name: 'NewsflashAdminQueueDetail', params: { id: item.id }, query: { section: 'queue' } }" class="queue-link">
              <div class="queue-item-main">
                <span class="status-chip">{{ item.status_label }}</span>
                <h2>{{ item.title }}</h2>
                <p>{{ item.summary }}</p>
              </div>
              <div class="queue-item-meta">
                <div>
                  <span>Fuente</span>
                  <strong>{{ item.media }}</strong>
                </div>
                <div>
                  <span>Volatilidad estimada</span>
                  <strong>{{ formatVolatility(item.volatility_percent) }}</strong>
                </div>
                <div>
                  <span>Tipo</span>
                  <strong>{{ item.news_type_label }}</strong>
                </div>
                <div>
                  <span>Fecha de generacion</span>
                  <strong>{{ formatTimestamp(item.created) }}</strong>
                </div>
                <div>
                  <span>Fecha de caducidad</span>
                  <strong>{{ formatTimestamp(item.expires_at) }}</strong>
                </div>
              </div>
            </router-link>
          </article>
        </div>
        </template>

        <template v-else-if="activeSection === 'ja-pending'">
          <div class="queue-summary">
            <div class="queue-summary-main">
              <strong>{{ jaPendingItems.length }}</strong>
              <span>noticias pendientes de JA</span>
            </div>
          </div>

          <p v-if="actionMessage" class="action-message queue-action-message">{{ actionMessage }}</p>

          <div v-if="!jaPendingItems.length" class="state-panel compact">
            <span class="material-icons-outlined state-icon">mark_email_read</span>
            <h2>No hay noticias pendientes de JA</h2>
            <p>Las noticias enviadas a JA apareceran aqui hasta que se publiquen o se rechacen.</p>
          </div>

          <div v-else class="queue-list">
            <article v-for="item in jaPendingItems" :key="item.id" class="queue-item queue-item-no-select">
              <router-link :to="{ name: 'NewsflashAdminQueueDetail', params: { id: item.id }, query: { section: 'ja-pending' } }" class="queue-link">
                <div class="queue-item-main">
                  <span class="status-chip">{{ item.status_label }}</span>
                  <h2>{{ item.title }}</h2>
                  <p>{{ item.summary }}</p>
                </div>
                <div class="queue-item-meta">
                  <div>
                    <span>Fuente</span>
                    <strong>{{ item.media }}</strong>
                  </div>
                  <div>
                    <span>Volatilidad estimada</span>
                    <strong>{{ formatVolatility(item.volatility_percent) }}</strong>
                  </div>
                  <div>
                    <span>Tipo</span>
                    <strong>{{ item.news_type_label }}</strong>
                  </div>
                  <div>
                    <span>Enviada a JA</span>
                    <strong>{{ formatTimestamp(item.sent_to_ja_at) }}</strong>
                  </div>
                  <div>
                    <span>Fecha de caducidad</span>
                    <strong>{{ formatTimestamp(item.expires_at) }}</strong>
                  </div>
                </div>
              </router-link>
            </article>
          </div>
        </template>

        <template v-else-if="activeSection === 'filtered'">
          <div class="queue-summary">
            <div class="queue-summary-main">
              <strong>{{ filteredItems.length }}</strong>
              <span>noticias descartadas por filtro</span>
            </div>
          </div>

          <p v-if="actionMessage" class="action-message queue-action-message">{{ actionMessage }}</p>

          <div v-if="!filteredItems.length" class="state-panel compact">
            <span class="material-icons-outlined state-icon">filter_alt</span>
            <h2>No hay noticias descartadas por filtro</h2>
            <p>Si la IA encuentra noticias que no superen el umbral apareceran aqui temporalmente.</p>
          </div>

          <div v-else class="queue-list">
            <article v-for="item in filteredItems" :key="item.id" class="queue-item queue-item-no-select">
              <router-link :to="{ name: 'NewsflashAdminQueueDetail', params: { id: item.id }, query: { section: 'filtered' } }" class="queue-link">
                <div class="queue-item-main">
                  <span class="status-chip">{{ item.status_label }}</span>
                  <h2>{{ item.title }}</h2>
                  <p>{{ item.summary }}</p>
                </div>
                <div class="queue-item-meta">
                  <div>
                    <span>Motivo</span>
                    <strong>{{ item.filter_reason_label || '-' }}</strong>
                  </div>
                  <div>
                    <span>Volatilidad estimada</span>
                    <strong>{{ formatVolatility(item.volatility_percent) }}</strong>
                  </div>
                  <div>
                    <span>Tipo</span>
                    <strong>{{ item.news_type_label }}</strong>
                  </div>
                  <div>
                    <span>Fecha de generacion</span>
                    <strong>{{ formatTimestamp(item.created) }}</strong>
                  </div>
                  <div>
                    <span>Visible hasta</span>
                    <strong>{{ formatTimestamp(item.expires_at) }}</strong>
                  </div>
                </div>
              </router-link>
            </article>
          </div>
        </template>

        <template v-else>
          <div class="queue-summary">
            <div class="queue-summary-main">
              <strong>{{ publishedItems.length }}</strong>
              <span>noticias publicadas</span>
            </div>
          </div>

          <p v-if="actionMessage" class="action-message queue-action-message">{{ actionMessage }}</p>

          <div v-if="!publishedItems.length" class="state-panel compact">
            <span class="material-icons-outlined state-icon">article</span>
            <h2>No hay noticias publicadas</h2>
            <p>Cuando JA publique noticias apareceran aqui.</p>
          </div>

          <div v-else class="published-list">
            <article v-for="item in publishedItems" :key="item.id" class="published-item">
              <router-link :to="item.public_path" class="published-link">
                <span class="status-chip">Publicada</span>
                <h2>{{ item.title }}</h2>
                <p>{{ item.summary }}</p>
                <div class="published-meta">
                  <span>{{ item.media }}</span>
                  <span>{{ formatTimestamp(item.published_at) }}</span>
                  <span>{{ formatVolatility(item.volatility_percent) }}</span>
                </div>
              </router-link>

              <button type="button" class="danger-button published-delete-button" :disabled="actionLoading" @click="deletePublishedItem(item)">
                <span class="material-icons-outlined">delete</span>
                Borrar
              </button>
            </article>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import newsflashAiApi from '@/services/newsflashAiApi';

export default {
  name: 'NewsflashAdmin',
  data() {
    return {
      items: [],
      jaPendingItems: [],
      filteredItems: [],
      publishedItems: [],
      currentItem: null,
      loading: false,
      actionLoading: false,
      errorMessage: '',
      actionMessage: '',
      activeSection: 'ja-pending',
    };
  },
  computed: {
    isJaReview() {
      return this.$route.name === 'NewsflashAdminJaReview';
    },
    isQueueDetail() {
      return this.$route.name === 'NewsflashAdminQueueDetail';
    },
    headerDescription() {
      if (this.isJaReview) {
        return 'Revision final de JA para aprobar o denegar la publicacion.';
      }

      return 'Seguimiento de noticias pendientes de JA, publicadas y capturadas por la IA.';
    },
    detailBackLabel() {
      if (this.$route.query.section === 'published') {
        return 'Volver a publicadas';
      }

      if (this.$route.query.section === 'filtered') {
        return 'Volver a descartadas';
      }

      return this.$route.query.section === 'queue' ? 'Volver a capturadas sin enviar' : 'Volver a pendientes JA';
    },
  },
  watch: {
    '$route.fullPath'() {
      this.captureAccessKey();
      this.syncActiveSectionFromRoute();
      this.loadCurrentView();
    },
  },
  mounted() {
    this.captureAccessKey();
    this.syncActiveSectionFromRoute();
    this.loadCurrentView();
  },
  methods: {
    normalizeSection(section) {
      return ['queue', 'ja-pending', 'published', 'filtered'].indexOf(section) !== -1 ? section : 'ja-pending';
    },
    syncActiveSectionFromRoute() {
      if (this.$route.name !== 'NewsflashAdmin') {
        return;
      }

      this.activeSection = this.normalizeSection(this.$route.query.section);
    },
    captureAccessKey() {
      const key = this.$route.query.k;
      if (!key) {
        return;
      }

      newsflashAiApi.storeAccessKey(key);
      const query = { ...this.$route.query };
      delete query.k;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    async loadCurrentView(preserveActionMessage = false) {
      this.loading = true;
      this.errorMessage = '';
      if (preserveActionMessage !== true) {
        this.actionMessage = '';
      }

      try {
        if (this.isJaReview) {
          const response = await newsflashAiApi.getJaItem(this.$route.params.id);
          this.currentItem = response.data;
          this.items = [];
        }
        else if (this.isQueueDetail) {
          const response = await newsflashAiApi.getQueueItem(this.$route.params.id);
          this.currentItem = response.data;
          this.items = [];
        }
        else {
          if (this.activeSection === 'published') {
            await this.loadPublishedItems();
          }
          else if (this.activeSection === 'ja-pending') {
            await this.loadJaPendingItems();
          }
          else if (this.activeSection === 'filtered') {
            await this.loadFilteredItems();
          }
          else {
            const response = await newsflashAiApi.listQueue();
            this.items = response.data && Array.isArray(response.data.items) ? response.data.items : [];
          }
          this.currentItem = null;
        }
      }
      catch (error) {
        this.errorMessage = this.resolveErrorMessage(error);
        this.currentItem = null;
        this.items = [];
      }
      finally {
        this.loading = false;
      }
    },
    async loadPublishedItems() {
      const response = await newsflashAiApi.listPublished();
      this.publishedItems = Array.isArray(response.data) ? response.data : [];
    },
    async loadJaPendingItems() {
      const response = await newsflashAiApi.listJaPending();
      this.jaPendingItems = response.data && Array.isArray(response.data.items) ? response.data.items : [];
    },
    async loadFilteredItems() {
      const response = await newsflashAiApi.listFiltered();
      this.filteredItems = response.data && Array.isArray(response.data.items) ? response.data.items : [];
    },
    async setActiveSection(section) {
      section = this.normalizeSection(section);
      if (this.activeSection === section) {
        return;
      }

      this.activeSection = section;
      if (this.$route.name === 'NewsflashAdmin') {
        const query = section === 'ja-pending' ? {} : { section };
        await this.$router.replace({ name: 'NewsflashAdmin', query }).catch(() => {});
        return;
      }
      await this.loadCurrentView();
    },
    async runCapture() {
      if (!window.confirm('Ejecutar la captacion IA ahora?')) {
        return;
      }

      await this.performAction(() => newsflashAiApi.runCapture(), true);
    },
    async clearQueue() {
      if (!window.confirm('Borrar todas las noticias pendientes, descartadas y capturadas no publicadas? Las noticias publicadas no se tocaran.')) {
        return;
      }

      await this.performAction(() => newsflashAiApi.clearQueue(), true);
    },
    async deletePublishedItem(item) {
      if (!item || !window.confirm(`Borrar la noticia publicada "${item.title}"?`)) {
        return;
      }

      this.actionLoading = true;
      this.actionMessage = '';

      try {
        const response = await newsflashAiApi.deletePublished(item.id);
        this.actionMessage = response.data && response.data.message ? response.data.message : 'Noticia publicada borrada.';
        await this.loadPublishedItems();
      }
      catch (error) {
        this.actionMessage = this.resolveErrorMessage(error);
      }
      finally {
        this.actionLoading = false;
      }
    },
    async approveCurrent() {
      if (!window.confirm('Aprobar y publicar esta noticia?')) {
        return;
      }

      await this.performAction(() => newsflashAiApi.approveJa(this.currentItem.id), false);
    },
    async rejectCurrent() {
      if (!window.confirm('Denegar esta noticia?')) {
        return;
      }

      await this.performAction(() => newsflashAiApi.rejectJa(this.currentItem.id), false);
    },
    async performAction(request, reloadAfter = true, afterSuccess = null) {
      this.actionLoading = true;
      this.actionMessage = '';

      try {
        const response = await request();
        const message = response.data && response.data.message ? response.data.message : 'Accion completada.';

        if (typeof afterSuccess === 'function') {
          await afterSuccess();
        }
        else if (reloadAfter) {
          await this.loadCurrentView(true);
        }

        this.actionMessage = message;
      }
      catch (error) {
        this.actionMessage = this.resolveErrorMessage(error);
      }
      finally {
        this.actionLoading = false;
      }
    },
    goToQueue() {
      const section = this.normalizeSection(this.$route.query.section);
      const query = section === 'ja-pending' ? {} : { section };
      this.$router.push({ name: 'NewsflashAdmin', query });
    },
    formatVolatility(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '-';
      }

      return `${new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(number)}%`;
    },
    formatHistoricalHighTicker(ticker) {
      if (!ticker) {
        return '-';
      }

      const extreme = ticker.tipo_extremo_label || 'Extremo historico';
      const days = Number.isFinite(Number(ticker.dias_desde_extremo)) ? `, hace ${ticker.dias_desde_extremo} dias` : '';
      const date = ticker.fecha_extremo_historico ? `, ${ticker.fecha_extremo_historico}` : '';
      return `${ticker.ticker} - ${ticker.nombre_empresa} (${extreme}${days}${date})`;
    },
    formatTimestamp(value) {
      if (!value) {
        return '-';
      }

      return this.formatDate(Number(value) * 1000);
    },
    formatDate(value) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return value || '-';
      }

      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },
    resolveErrorMessage(error) {
      const response = error && error.response ? error.response : null;
      if (response && response.data && response.data.message) {
        return response.data.message;
      }

      if (response && response.status === 403) {
        return 'No tienes permisos o la clave temporal ha caducado.';
      }

      if (response && response.status === 404) {
        return 'La noticia no existe o ya no esta disponible.';
      }

      return 'No se pudo completar la operacion.';
    },
  },
  metaInfo() {
    return {
      title: 'Gestion noticias flash | Eurekers',
    };
  },
};
</script>

<style lang="scss" scoped>
.newsflash-admin-page {
  min-height: 100vh;
  background: var(--color-background);
  color: var(--color-text-primary);
}

.newsflash-admin-container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 24px;
}

.admin-header,
.detail-layout,
.queue-panel {
  display: grid;
  gap: 18px;
}

.admin-header {
  grid-template-columns: 1fr auto;
  align-items: end;
  margin-bottom: 22px;

  h1 {
    margin: 0 0 8px;
    color: var(--color-primary);
    font-size: 30px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: var(--color-text-secondary);
  }
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.primary-button,
.secondary-button,
.danger-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 9px 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.primary-button {
  background: var(--color-highlight);
  color: var(--color-text-on-highlight);
}

.secondary-button {
  background: var(--color-surface);
  color: var(--color-text-primary);
  border-color: var(--color-outline);
}

.danger-button {
  background: #b91c1c;
  color: #fff;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.full-width {
  width: 100%;
}

.state-panel,
.detail-card,
.actions-panel,
.queue-summary,
.queue-item {
  background: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;
}

.state-panel {
  display: flex;
  min-height: 260px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 28px;
  text-align: center;

  h2,
  p {
    margin: 0;
  }
}

.compact {
  min-height: 180px;
}

.loading-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid var(--color-outline);
  border-top-color: var(--color-highlight);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.state-icon {
  color: var(--color-error);
  font-size: 34px;
}

.detail-layout {
  grid-template-columns: minmax(0, 1fr) 280px;
  align-items: start;
}

.detail-card,
.actions-panel {
  padding: 20px;
}

.detail-topbar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-card h2 {
  margin: 0 0 18px;
  color: var(--color-primary);
  font-size: 24px;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  div {
    padding: 12px;
    background: var(--color-background);
    border-radius: 8px;
  }

  span {
    display: block;
    margin-bottom: 5px;
    color: var(--color-text-secondary);
    font-size: 12px;
  }
}

.source-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
  color: var(--color-highlight);
  font-weight: 700;
  text-decoration: none;
}

.content-block {
  padding: 16px;
  margin-top: 14px;
  background: var(--color-background);
  border: 1px solid var(--color-outline);
  border-radius: 8px;

  h3 {
    margin: 0 0 10px;
    color: var(--color-primary);
  }

  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.6;
    white-space: pre-line;
  }
}

.internal-analysis-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;

  div {
    padding: 10px;
    background: var(--color-surface);
    border: 1px solid var(--color-outline);
    border-radius: 8px;
  }

  span {
    display: block;
    margin-bottom: 4px;
    color: var(--color-text-secondary);
    font-size: 12px;
  }
}

.comparable-event {
  display: grid;
  gap: 6px;
  padding: 12px;
  margin-bottom: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;

  span {
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  p {
    margin: 0;
  }

  a {
    color: var(--color-highlight);
    font-weight: 700;
    text-decoration: none;
  }
}

.analysis-note {
  padding-top: 10px;
  border-top: 1px solid var(--color-outline);
}

.stocks-list {
  display: grid;
  gap: 10px;
}

.stock-row {
  padding: 12px;
  background: var(--color-surface);
  border-radius: 8px;
}

.stock-head {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: var(--color-active-surface);
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 700;
}

.actions-panel {
  display: grid;
  gap: 12px;

  h3 {
    margin: 0;
  }
}

.action-message {
  margin: 0;
  color: var(--color-text-secondary);
}

.action-note {
  margin: 0;
  padding: 12px;
  border: 1px solid var(--color-outline);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.admin-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 38px;
    padding: 8px 12px;
    border: 1px solid var(--color-outline);
    border-radius: 8px;
    background: var(--color-surface);
    color: var(--color-text-secondary);
    font-weight: 700;
    cursor: pointer;
  }

  button.active {
    border-color: var(--color-highlight);
    background: var(--color-active-surface);
    color: var(--color-text-primary);
  }
}

.queue-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;

  strong {
    color: var(--color-highlight);
    font-size: 26px;
  }
}

.queue-summary-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.queue-action-message {
  padding: 0 2px;
}

.queue-list {
  display: grid;
  gap: 12px;
}

.published-list {
  display: grid;
  gap: 12px;
}

.published-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;
}

.published-link {
  display: grid;
  gap: 8px;
  color: inherit;
  text-decoration: none;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: var(--color-primary);
    font-size: 18px;
  }

  p {
    color: var(--color-text-secondary);
    line-height: 1.5;
  }
}

.published-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: var(--color-text-secondary);
  font-size: 13px;
}

.published-delete-button {
  white-space: nowrap;
}

.queue-item {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  align-items: stretch;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.queue-item-no-select {
  grid-template-columns: minmax(0, 1fr);
}

.queue-link {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 250px;
  gap: 16px;
  padding: 16px;
  color: inherit;
  text-decoration: none;
}

.queue-item-main {
  display: grid;
  gap: 8px;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: var(--color-primary);
    font-size: 18px;
  }

  p {
    color: var(--color-text-secondary);
    line-height: 1.5;
  }
}

.queue-item-meta {
  display: grid;
  align-content: center;
  gap: 8px;
  color: var(--color-text-secondary);
  text-align: right;

  div {
    display: grid;
    gap: 2px;
  }

  span {
    font-size: 12px;
  }

  strong {
    color: var(--color-text-primary);
    font-size: 13px;
  }
}

.empty-text {
  color: var(--color-text-secondary);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .admin-header,
  .detail-layout,
  .queue-link {
    grid-template-columns: 1fr;
  }

  .published-item {
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .published-delete-button {
    width: fit-content;
  }

  .queue-summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .queue-item-meta {
    text-align: left;
  }

  .meta-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .newsflash-admin-container {
    padding: 18px 14px;
  }

  .queue-item,
  .queue-item-no-select {
    grid-template-columns: minmax(0, 1fr);
  }

  .admin-tabs,
  .admin-tabs button,
  .published-delete-button {
    width: 100%;
  }

  .internal-analysis-grid {
    grid-template-columns: 1fr;
  }

  .meta-grid {
    grid-template-columns: 1fr;
  }
}
</style>
